import React from 'react';
import type { User, XeroTenant } from '../types';
import mascotLogo from '../../../assets/AppMascot.webp';

export type AppView = 'main' | 'past-runs' | 'settings' | 'admin';

interface LayoutProps {
  user: User | null;
  xeroConnected: boolean;
  xeroTenants: XeroTenant[];
  view: AppView;
  onNavigate: (view: AppView) => void;
  children: React.ReactNode;
  onLogin: () => void;
  onSignOut: () => void | Promise<void>;
}

export default function Layout({
  user,
  xeroConnected,
  xeroTenants,
  view,
  onNavigate,
  children,
  onLogin,
  onSignOut,
}: LayoutProps) {
  const activeTenant = xeroTenants.find((t) => t.isActive);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Left Navigation */}
      <aside className="flex w-64 flex-shrink-0 flex-col border-r border-[#4ca665] bg-[#58b873] text-white">
        {/* Brand */}
        <div className="border-b border-white/10 px-4 py-4">
          <div className="mb-3 overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/15">
            <img
              src={mascotLogo}
              alt="Contractor Super logo"
              className="aspect-square w-full object-cover"
            />
          </div>
          <div>
            <div className="text-sm font-semibold text-white leading-tight">Contractor Super</div>
            <div className="text-xs font-medium text-white/70 leading-tight">Payroll workflow</div>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-0.5">
            <NavItem
              icon={<UploadIcon />}
              label="Import Report"
              active={view === 'main'}
              onClick={() => onNavigate('main')}
            />
            <NavItem
              icon={<ClockIcon />}
              label="Past Runs"
              active={view === 'past-runs'}
              onClick={() => onNavigate('past-runs')}
              disabled={!user}
            />
            <NavItem
              icon={<CogIcon />}
              label="Settings"
              active={view === 'settings'}
              onClick={() => onNavigate('settings')}
              disabled={!user}
            />
            {user?.isAdmin && (
              <NavItem
                icon={<ShieldIcon />}
                label="Admin"
                active={view === 'admin'}
                onClick={() => onNavigate('admin')}
              />
            )}
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-white/10 p-4 space-y-4">
          <p className="text-[10px] text-white/50 font-mono" title="Build version">Build {__BUILD_ID__}</p>

          {/* Xero status — display-only. Connect/disconnect happens in InvoicePolish. */}
          {user && xeroConnected && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-white/80 flex-shrink-0" />
                <span className="text-xs text-white/85 font-medium">Xero connected</span>
              </div>
              {activeTenant && (
                <p className="text-xs text-white/65 pl-3 truncate">{activeTenant.tenantName}</p>
              )}
            </div>
          )}

          {/* User */}
          {user ? (
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold uppercase text-white ring-1 ring-white/10">
                {(user.email ?? user.name ?? '?').charAt(0)}
              </div>
              <span className="flex-1 truncate text-xs text-white/80">{user.email ?? user.name ?? `User ${user.id}`}</span>
              <button
                type="button"
                onClick={onSignOut}
                title="Sign out"
                aria-label="Sign out"
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/15 hover:text-white"
              >
                <LogoutIcon />
              </button>
            </div>
          ) : (
            <button onClick={onLogin} className="btn-primary w-full text-xs py-2">
              Sign In
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex flex-1 flex-col overflow-y-auto">
        {children}
      </main>
    </div>
  );
}

function NavItem({
  icon,
  label,
  active = false,
  disabled = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={[
        'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors text-left',
        active
          ? 'bg-white text-[#3f9257] shadow-sm'
          : disabled
          ? 'text-white/45 cursor-not-allowed'
          : 'text-white/85 hover:bg-white/20 hover:text-white',
      ].join(' ')}
    >
      <span className={active ? 'text-[#3f9257]' : 'text-white/70'}>{icon}</span>
      {label}
    </button>
  );
}

function UploadIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M3 4.75A1.75 1.75 0 014.75 3h6.5A1.75 1.75 0 0113 4.75v2a.75.75 0 01-1.5 0v-2a.25.25 0 00-.25-.25h-6.5a.25.25 0 00-.25.25v10.5c0 .138.112.25.25.25h6.5a.25.25 0 00.25-.25v-2a.75.75 0 011.5 0v2A1.75 1.75 0 0111.25 17h-6.5A1.75 1.75 0 013 15.25V4.75z" clipRule="evenodd" />
      <path fillRule="evenodd" d="M14.47 6.47a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H8.75a.75.75 0 010-1.5h7.44l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM13.707 8.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
}

function CogIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
    </svg>
  );
}
